import React, { useEffect, useRef, useState } from "react";
import { InputNumber, Select, Button, Switch, Divider, Input } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { DevType } from "../../../flatbuffers/generated/front-end/dev-type";
import NumatoFlatMessageFields from "./NumatoFlatMessageFields";
import {
  type NumatoFlatMessage,
  buildDefaultNumatoFlatMessage,
  readNumatoFlatMessage,
} from "../../../flatbuffers/types/numatoFlatMessage";
import {
  type U128,
  defaultU128,
  renderU128Fields,
  U128_VALUE_LABEL,
  U128_VALUE_0_LABEL,
  U128_VALUE_1_LABEL,
} from "./numatoU128Fields";

// ************************************************ */
// TYPE DEFINITIONS ******************************* */
type IdContent = { kind: "GetMessage" } | { kind: "SetMessage"; message: number };

type GpioContent =
  | { kind: "ReadMessage"; message: number }
  | { kind: "ClearMessage"; message: number }
  | { kind: "SetMessage"; message: number }
  | { kind: "WriteAllMessage"; value: U128 }
  | { kind: "ReadAllMessage" }
  | { kind: "IoDirection"; value: U128 }
  | { kind: "IoMask"; value: U128 }
  | { kind: "SetNotify"; value: boolean }
  | { kind: "GetNotify" }
  | { kind: "PowerOn"; value_0: U128; value_1: U128 };

type AdcContent = { kind: "ReadMessage"; message: number };

type GpioCommand =
  | { kind: "InitMessage" }
  | { kind: "InfoMessage" }
  | { kind: "IdCommandContentTable"; content: IdContent }
  | { kind: "GpioCommandContentTable"; content: GpioContent }
  | { kind: "VersionMessage" }
  | { kind: "AdcCommandContentTable"; content: AdcContent };

type GpioResponse =
  | { kind: "StringResponse"; message: string }
  | { kind: "NumericResponse"; message: number }
  | { kind: "BoolResponse"; message: boolean }
  | { kind: "TupleResponse"; val_0: number; val_1: number }
  | { kind: "NoResponse" };

interface NumatoLookupValue {
  dev_type: number;
  bank_id: string;
  lift_id: string;
  timestamp: number;
  derived: boolean;
  msg: NumatoFlatMessage;
}

interface ResponseToMessageEntry {
  key: { command: GpioCommand; response: GpioResponse };
  value: NumatoLookupValue;
}

interface MessageToCommandEntry {
  key: NumatoFlatMessage;
  value: GpioCommand;
}

interface NumatoGpioCdo {
  response_to_message: ResponseToMessageEntry[];
  message_to_cmd: MessageToCommandEntry[];
}

// ************************************************ */
// COMPONENT PROPS ******************************** */
export interface BankContextInfo {
  bankId: string;
  bankName: string;
  lifts: { id: string; name: string }[];
}

interface CdoNumatoGpioEditorProps {
  jsonValue: string;
  onChange: (json: string) => void;
  bankContext?: BankContextInfo;
  defaultDevType?: number;
}

// ************************************************ */
// CONSTANTS ************************************** */
const COMMAND_CATEGORIES = [
  { label: "Init Message", value: "InitMessage" },
  { label: "Info Message", value: "InfoMessage" },
  { label: "ID Command", value: "IdCommandContentTable" },
  { label: "GPIO Command", value: "GpioCommandContentTable" },
  { label: "Version Message", value: "VersionMessage" },
  { label: "ADC Command", value: "AdcCommandContentTable" },
];

const GPIO_SUB_TYPES = [
  { label: "Read", value: "ReadMessage" },
  { label: "Clear", value: "ClearMessage" },
  { label: "Set", value: "SetMessage" },
  { label: "Write All", value: "WriteAllMessage" },
  { label: "Read All", value: "ReadAllMessage" },
  { label: "IO Direction", value: "IoDirection" },
  { label: "IO Mask", value: "IoMask" },
  { label: "Set Notify", value: "SetNotify" },
  { label: "Get Notify", value: "GetNotify" },
  { label: "Power On", value: "PowerOn" },
];

const RESPONSE_TYPES = [
  { label: "String Response", value: "StringResponse" },
  { label: "Numeric Response", value: "NumericResponse" },
  { label: "Bool Response", value: "BoolResponse" },
  { label: "Tuple Response", value: "TupleResponse" },
  { label: "No Response", value: "NoResponse" },
];

const DEV_TYPES = Object.entries(DevType)
  .filter(([, v]) => typeof v === "number")
  .map(([label, value]) => ({ label: `${label} (${value})`, value: value as number }));

const ID_SUB_TYPES = [
  { label: "Get Message", value: "GetMessage" },
  { label: "Set Message", value: "SetMessage" },
];

// ************************************************ */
// STYLES ***************************************** */
const entryStyle: React.CSSProperties = {
  border: "1px solid #303030",
  borderRadius: 6,
  padding: 12,
  marginBottom: 12,
  position: "relative",
};

const deleteButtonStyle: React.CSSProperties = {
  position: "absolute",
  top: 8,
  right: 8,
};

const sectionLabelStyle: React.CSSProperties = {
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: 1,
  color: "#888",
};

// ************************************************ */
// DEFAULT BUILDERS ******************************* */
function buildDefaultGpioContent(subKind: string): GpioContent {
  switch (subKind) {
    case "ReadMessage":
      return { kind: "ReadMessage", message: 0 };
    case "ClearMessage":
      return { kind: "ClearMessage", message: 0 };
    case "SetMessage":
      return { kind: "SetMessage", message: 0 };
    case "WriteAllMessage":
      return { kind: "WriteAllMessage", value: defaultU128() };
    case "ReadAllMessage":
      return { kind: "ReadAllMessage" };
    case "IoDirection":
      return { kind: "IoDirection", value: defaultU128() };
    case "IoMask":
      return { kind: "IoMask", value: defaultU128() };
    case "SetNotify":
      return { kind: "SetNotify", value: false };
    case "GetNotify":
      return { kind: "GetNotify" };
    case "PowerOn":
      return {
        kind: "PowerOn",
        value_0: defaultU128(),
        value_1: defaultU128(),
      };
    default:
      return { kind: "ReadMessage", message: 0 };
  }
}

function buildDefaultCommand(kind: string): GpioCommand {
  switch (kind) {
    case "InitMessage":
      return { kind: "InitMessage" };
    case "InfoMessage":
      return { kind: "InfoMessage" };
    case "IdCommandContentTable":
      return {
        kind: "IdCommandContentTable",
        content: { kind: "GetMessage" },
      };
    case "GpioCommandContentTable":
      return {
        kind: "GpioCommandContentTable",
        content: { kind: "ReadMessage", message: 0 },
      };
    case "VersionMessage":
      return { kind: "VersionMessage" };
    case "AdcCommandContentTable":
      return {
        kind: "AdcCommandContentTable",
        content: { kind: "ReadMessage", message: 0 },
      };
    default:
      return { kind: "InitMessage" };
  }
}

function buildDefaultResponse(kind: string): GpioResponse {
  switch (kind) {
    case "StringResponse":
      return { kind: "StringResponse", message: "" };
    case "NumericResponse":
      return { kind: "NumericResponse", message: 0 };
    case "BoolResponse":
      return { kind: "BoolResponse", message: false };
    case "TupleResponse":
      return { kind: "TupleResponse", val_0: 0, val_1: 0 };
    case "NoResponse":
      return { kind: "NoResponse" };
    default:
      return { kind: "NoResponse" };
  }
}

function buildDefaultLookupValue(devType: number): NumatoLookupValue {
  return {
    dev_type: devType,
    bank_id: "",
    lift_id: "",
    timestamp: 0,
    derived: false,
    msg: buildDefaultNumatoFlatMessage(),
  };
}

function buildDefaultCdo(): NumatoGpioCdo {
  return { response_to_message: [], message_to_cmd: [] };
}

// ************************************************ */
// COMMAND FIELD RENDERER ************************* */
function renderCommandFields(cmd: GpioCommand, onChangeCmd: (updated: GpioCommand) => void) {
  if (cmd.kind === "IdCommandContentTable") {
    const idContent = cmd.content as IdContent;
    return (
      <>
        <div className="row">
          <div className="col-12 col-md-6 mt-2">
            <span className="generalStyles__inputLabel">ID Sub-Type</span>
            <Select
              className="generalStyles__width100"
              size="small"
              value={idContent.kind}
              onChange={(val: string) => {
                const newContent: IdContent =
                  val === "SetMessage"
                    ? { kind: "SetMessage", message: 0 }
                    : { kind: "GetMessage" };
                onChangeCmd({
                  ...cmd,
                  content: newContent,
                } as GpioCommand);
              }}
              style={{ marginTop: 4 }}
            >
              {ID_SUB_TYPES.map((opt) => (
                <Select.Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Select.Option>
              ))}
            </Select>
          </div>
        </div>
        {idContent.kind === "SetMessage" && (
          <div className="row">
            <div className="col-12 col-md-6 mt-2">
              <span className="generalStyles__inputLabel">Message (uint8)</span>
              <InputNumber
                className="generalStyles__width100"
                size="small"
                min={0}
                max={255}
                value={(idContent as { message: number }).message}
                onChange={(v) =>
                  onChangeCmd({
                    ...cmd,
                    content: {
                      kind: "SetMessage",
                      message: v ?? 0,
                    },
                  } as GpioCommand)
                }
                style={{ marginTop: 4 }}
              />
            </div>
          </div>
        )}
      </>
    );
  }

  if (cmd.kind === "GpioCommandContentTable") {
    const gpioContent = cmd.content as GpioContent;
    return (
      <>
        <div className="row">
          <div className="col-12 col-md-6 mt-2">
            <span className="generalStyles__inputLabel">GPIO Sub-Type</span>
            <Select
              className="generalStyles__width100"
              size="small"
              value={gpioContent.kind}
              onChange={(val: string) =>
                onChangeCmd({
                  ...cmd,
                  content: buildDefaultGpioContent(val),
                } as GpioCommand)
              }
              style={{ marginTop: 4 }}
            >
              {GPIO_SUB_TYPES.map((opt) => (
                <Select.Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Select.Option>
              ))}
            </Select>
          </div>
        </div>
        {renderGpioContentFields(gpioContent, (updated) =>
          onChangeCmd({
            ...cmd,
            content: updated,
          } as GpioCommand),
        )}
      </>
    );
  }

  if (cmd.kind === "AdcCommandContentTable") {
    const adcContent = cmd.content as AdcContent;
    return (
      <div className="row">
        <div className="col-12 col-md-6 mt-2">
          <span className="generalStyles__inputLabel">Message (uint8)</span>
          <InputNumber
            className="generalStyles__width100"
            size="small"
            min={0}
            max={255}
            value={adcContent.message}
            onChange={(v) =>
              onChangeCmd({
                ...cmd,
                content: {
                  kind: "ReadMessage",
                  message: v ?? 0,
                },
              } as GpioCommand)
            }
            style={{ marginTop: 4 }}
          />
        </div>
      </div>
    );
  }

  // InitMessage, InfoMessage, VersionMessage — no fields
  return <p style={{ color: "#888", margin: "8px 0 0" }}>No additional fields required.</p>;
}

// ************************************************ */
// GPIO CONTENT FIELD RENDERER ******************** */
function renderGpioContentFields(
  content: GpioContent,
  onChangeContent: (updated: GpioContent) => void,
) {
  const { kind } = content;

  // uint8 message field
  if (kind === "ReadMessage" || kind === "ClearMessage" || kind === "SetMessage") {
    return (
      <div className="row">
        <div className="col-12 col-md-6 mt-2">
          <span className="generalStyles__inputLabel">Message (uint8)</span>
          <InputNumber
            className="generalStyles__width100"
            size="small"
            min={0}
            max={255}
            value={(content as { message: number }).message}
            onChange={(v) =>
              onChangeContent({
                ...content,
                message: v ?? 0,
              } as GpioContent)
            }
            style={{ marginTop: 4 }}
          />
        </div>
      </div>
    );
  }

  // Single U128 value. A missing `value` (a legacy or hand-edited CDO) is
  // not a problem — LiftCloud defaults it to zero, so it renders as 0/0
  // rather than crashing (N2).
  if (kind === "WriteAllMessage" || kind === "IoDirection" || kind === "IoMask") {
    const { value } = content as { value?: U128 };
    return renderU128Fields(U128_VALUE_LABEL, value ?? defaultU128(), (updated) =>
      onChangeContent({ ...content, value: updated } as GpioContent),
    );
  }

  // SetNotify — bool
  if (kind === "SetNotify") {
    return (
      <div className="row">
        <div className="col-12 col-md-6 mt-2">
          <span className="generalStyles__inputLabel">Value</span>
          <div style={{ marginTop: 8 }}>
            <Switch
              checked={(content as { value: boolean }).value}
              onChange={(checked) =>
                onChangeContent({
                  ...content,
                  value: checked,
                } as GpioContent)
              }
            />
          </div>
        </div>
      </div>
    );
  }

  // PowerOn — two U128 values. Same N2 defaulting as above, independently
  // per half.
  if (kind === "PowerOn") {
    const powerContent = content as {
      kind: "PowerOn";
      value_0?: U128;
      value_1?: U128;
    };
    return (
      <>
        {renderU128Fields(U128_VALUE_0_LABEL, powerContent.value_0 ?? defaultU128(), (updated) =>
          onChangeContent({
            kind: "PowerOn",
            value_0: updated,
            value_1: powerContent.value_1 ?? defaultU128(),
          }),
        )}
        {renderU128Fields(U128_VALUE_1_LABEL, powerContent.value_1 ?? defaultU128(), (updated) =>
          onChangeContent({
            kind: "PowerOn",
            value_0: powerContent.value_0 ?? defaultU128(),
            value_1: updated,
          }),
        )}
      </>
    );
  }

  // ReadAllMessage, GetNotify — no fields
  return <p style={{ color: "#888", margin: "8px 0 0" }}>No additional fields required.</p>;
}

// ************************************************ */
// RESPONSE FIELD RENDERER ************************ */
function renderResponseFields(resp: GpioResponse, onChangeResp: (updated: GpioResponse) => void) {
  if (resp.kind === "StringResponse") {
    return (
      <div className="row">
        <div className="col-12 col-md-6 mt-2">
          <span className="generalStyles__inputLabel">Message</span>
          <Input
            size="small"
            value={resp.message}
            onChange={(e) =>
              onChangeResp({
                ...resp,
                message: e.target.value,
              })
            }
            style={{ marginTop: 4 }}
          />
        </div>
      </div>
    );
  }

  if (resp.kind === "NumericResponse") {
    return (
      <div className="row">
        <div className="col-12 col-md-6 mt-2">
          <span className="generalStyles__inputLabel">Message</span>
          <InputNumber
            className="generalStyles__width100"
            size="small"
            min={0}
            value={resp.message}
            onChange={(v) =>
              onChangeResp({
                ...resp,
                message: v ?? 0,
              })
            }
            style={{ marginTop: 4 }}
          />
        </div>
      </div>
    );
  }

  if (resp.kind === "BoolResponse") {
    return (
      <div className="row">
        <div className="col-12 col-md-6 mt-2">
          <span className="generalStyles__inputLabel">Message</span>
          <div style={{ marginTop: 8 }}>
            <Switch
              checked={resp.message}
              onChange={(checked) =>
                onChangeResp({
                  ...resp,
                  message: checked,
                })
              }
            />
          </div>
        </div>
      </div>
    );
  }

  if (resp.kind === "TupleResponse") {
    return (
      <div className="row">
        <div className="col-6 mt-2">
          <span className="generalStyles__inputLabel">Val 0</span>
          <InputNumber
            className="generalStyles__width100"
            size="small"
            min={0}
            value={resp.val_0}
            onChange={(v) => onChangeResp({ ...resp, val_0: v ?? 0 })}
            style={{ marginTop: 4 }}
          />
        </div>
        <div className="col-6 mt-2">
          <span className="generalStyles__inputLabel">Val 1</span>
          <InputNumber
            className="generalStyles__width100"
            size="small"
            min={0}
            value={resp.val_1}
            onChange={(v) => onChangeResp({ ...resp, val_1: v ?? 0 })}
            style={{ marginTop: 4 }}
          />
        </div>
      </div>
    );
  }

  // NoResponse
  return <p style={{ color: "#888", margin: "8px 0 0" }}>No additional fields required.</p>;
}

/**
 * Parse a persisted CDO string into typed state, coercing every message
 * through readNumatoFlatMessage so a legacy nested-union payload (or any
 * malformed entry) hydrates to "not configured" instead of throwing.
 */
function hydrate(raw: string): NumatoGpioCdo {
  try {
    const parsed = JSON.parse(raw) as Partial<NumatoGpioCdo>;
    return {
      response_to_message: (parsed.response_to_message ?? []).map((entry) => ({
        ...entry,
        value: {
          dev_type: entry.value?.dev_type ?? 3,
          bank_id: typeof entry.value?.bank_id === "string" ? entry.value.bank_id : "",
          lift_id: typeof entry.value?.lift_id === "string" ? entry.value.lift_id : "",
          timestamp: entry.value?.timestamp ?? 0,
          derived: entry.value?.derived ?? false,
          msg: readNumatoFlatMessage((entry.value as { msg?: unknown })?.msg),
        },
      })),
      message_to_cmd: (parsed.message_to_cmd ?? []).map((entry) => ({
        ...entry,
        key: readNumatoFlatMessage(entry.key),
      })),
    };
  } catch {
    return buildDefaultCdo();
  }
}

// ************************************************ */
// COMPONENT ************************************** */
export default function CdoNumatoGpioEditor({
  jsonValue,
  onChange,
  bankContext = undefined,
  defaultDevType = undefined,
}: CdoNumatoGpioEditorProps) {
  const [cdo, setCdoState] = useState<NumatoGpioCdo>(() => hydrate(jsonValue));

  const lastEmittedRef = useRef<string>(jsonValue);

  // Sync external jsonValue changes into internal state
  useEffect(() => {
    if (jsonValue === lastEmittedRef.current) return;
    if (!jsonValue.trim()) return;
    setCdoState(hydrate(jsonValue));
  }, [jsonValue]);

  // Emit changes to parent
  const emitChange = (updated: NumatoGpioCdo) => {
    setCdoState(updated);
    const json = JSON.stringify(updated, null, 2);
    lastEmittedRef.current = json;
    onChange(json);
  };

  // ************************************************ */
  // RESPONSE TO MESSAGE HELPERS ******************** */
  const updateR2M = (
    index: number,
    updater: (e: ResponseToMessageEntry) => ResponseToMessageEntry,
  ) => {
    const updated = cdo.response_to_message.map((entry, i) =>
      i === index ? updater(structuredClone(entry)) : entry,
    );
    emitChange({ ...cdo, response_to_message: updated });
  };

  const addR2M = () => {
    const newEntry: ResponseToMessageEntry = {
      key: {
        command: buildDefaultCommand("InitMessage"),
        response: buildDefaultResponse("NoResponse"),
      },
      value: buildDefaultLookupValue(defaultDevType ?? 3),
    };
    emitChange({
      ...cdo,
      response_to_message: [...cdo.response_to_message, newEntry],
    });
  };

  const removeR2M = (index: number) => {
    emitChange({
      ...cdo,
      response_to_message: cdo.response_to_message.filter((_, i) => i !== index),
    });
  };

  // ************************************************ */
  // MESSAGE TO COMMAND HELPERS ********************** */
  const updateM2C = (
    index: number,
    updater: (e: MessageToCommandEntry) => MessageToCommandEntry,
  ) => {
    const updated = cdo.message_to_cmd.map((entry, i) =>
      i === index ? updater(structuredClone(entry)) : entry,
    );
    emitChange({ ...cdo, message_to_cmd: updated });
  };

  const addM2C = () => {
    const newEntry: MessageToCommandEntry = {
      key: buildDefaultNumatoFlatMessage(),
      value: buildDefaultCommand("InitMessage"),
    };
    emitChange({
      ...cdo,
      message_to_cmd: [...cdo.message_to_cmd, newEntry],
    });
  };

  const removeM2C = (index: number) => {
    emitChange({
      ...cdo,
      message_to_cmd: cdo.message_to_cmd.filter((_, i) => i !== index),
    });
  };

  // ************************************************ */
  // RENDER ***************************************** */
  return (
    <div className="container">
      {/* ============================================= */}
      {/* RESPONSE TO MESSAGE MAPPINGS */}
      <Divider orientation="left" plain>
        Response to Message Mappings ({cdo.response_to_message.length})
      </Divider>
      {cdo.response_to_message.map((entry, idx) => (
        <div key={`r2m_${idx}`} style={entryStyle}>
          <Button
            type="text"
            danger
            size="small"
            style={deleteButtonStyle}
            icon={<FontAwesomeIcon icon={faTrash} />}
            onClick={() => removeR2M(idx)}
          />
          <strong style={{ display: "block", marginBottom: 8 }}>Mapping #{idx + 1}</strong>

          {/* COMMAND */}
          <span style={sectionLabelStyle}>Command</span>
          <div className="row">
            <div className="col-12 col-md-6 mt-2">
              <span className="generalStyles__inputLabel">Command Type</span>
              <Select
                className="generalStyles__width100"
                size="small"
                value={entry.key.command.kind}
                onChange={(val: string) =>
                  updateR2M(idx, (e) => ({
                    ...e,
                    key: {
                      ...e.key,
                      command: buildDefaultCommand(val),
                    },
                  }))
                }
                style={{ marginTop: 4 }}
              >
                {COMMAND_CATEGORIES.map((opt) => (
                  <Select.Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </div>
          {renderCommandFields(entry.key.command, (updatedCmd) =>
            updateR2M(idx, (e) => ({
              ...e,
              key: { ...e.key, command: updatedCmd },
            })),
          )}

          {/* RESPONSE */}
          <div style={{ marginTop: 12 }}>
            <span style={sectionLabelStyle}>Response</span>
            <div className="row">
              <div className="col-12 col-md-6 mt-2">
                <span className="generalStyles__inputLabel">Response Type</span>
                <Select
                  className="generalStyles__width100"
                  size="small"
                  value={entry.key.response.kind}
                  onChange={(val: string) =>
                    updateR2M(idx, (e) => ({
                      ...e,
                      key: {
                        ...e.key,
                        response: buildDefaultResponse(val),
                      },
                    }))
                  }
                  style={{ marginTop: 4 }}
                >
                  {RESPONSE_TYPES.map((opt) => (
                    <Select.Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            </div>
            {renderResponseFields(entry.key.response, (updatedResp) =>
              updateR2M(idx, (e) => ({
                ...e,
                key: {
                  ...e.key,
                  response: updatedResp,
                },
              })),
            )}
          </div>

          {/* LN2 DATA */}
          <div style={{ marginTop: 12 }}>
            <span style={sectionLabelStyle}>LN2 Data</span>
            <div className="row">
              {bankContext ? (
                <>
                  <div className="col-12 col-md-6 mt-2">
                    <span className="generalStyles__inputLabel">Bank</span>
                    <div
                      style={{
                        marginTop: 4,
                        padding: "4px 8px",
                        background: "#1f1f1f",
                        borderRadius: 4,
                      }}
                    >
                      {bankContext.bankName || bankContext.bankId}
                      <span
                        style={{
                          fontSize: 10,
                          color: "#888",
                          display: "block",
                        }}
                      >
                        Auto-populated from bank assignment
                      </span>
                    </div>
                  </div>
                  <div className="col-12 col-md-6 mt-2">
                    <span className="generalStyles__inputLabel">Lift</span>
                    <Select
                      className="generalStyles__width100"
                      size="small"
                      value={entry.value.lift_id || undefined}
                      placeholder="Select lift"
                      onChange={(val: string) => {
                        updateR2M(idx, (en) => ({
                          ...en,
                          value: {
                            ...en.value,
                            bank_id: bankContext.bankId,
                            lift_id: val,
                          },
                        }));
                      }}
                      allowClear
                      onClear={() =>
                        updateR2M(idx, (en) => ({
                          ...en,
                          value: { ...en.value, lift_id: "" },
                        }))
                      }
                      style={{ marginTop: 4 }}
                    >
                      {bankContext.lifts.map((l) => (
                        <Select.Option key={l.id} value={l.id}>
                          {l.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </div>
                </>
              ) : (
                <>
                  <div className="col-12 col-md-6 mt-2">
                    <span className="generalStyles__inputLabel">Bank ID</span>
                    <Input
                      size="small"
                      placeholder="UUID"
                      value={entry.value.bank_id}
                      onChange={(e) =>
                        updateR2M(idx, (en) => ({
                          ...en,
                          value: { ...en.value, bank_id: e.target.value },
                        }))
                      }
                      style={{ marginTop: 4 }}
                    />
                  </div>
                  <div className="col-12 col-md-6 mt-2">
                    <span className="generalStyles__inputLabel">Lift ID</span>
                    <Input
                      size="small"
                      placeholder="UUID"
                      value={entry.value.lift_id}
                      onChange={(e) =>
                        updateR2M(idx, (en) => ({
                          ...en,
                          value: { ...en.value, lift_id: e.target.value },
                        }))
                      }
                      style={{ marginTop: 4 }}
                    />
                    <span style={{ fontSize: 10, color: "#888", display: "block" }}>
                      Names are resolved for display from the bank, not stored in the CDO.
                    </span>
                  </div>
                </>
              )}
            </div>
            <div className="row">
              <div className="col-12 col-md-3 mt-2">
                <span className="generalStyles__inputLabel">Device Type</span>
                <Select
                  className="generalStyles__width100"
                  size="small"
                  value={entry.value.dev_type}
                  onChange={(val: number) =>
                    updateR2M(idx, (en) => ({
                      ...en,
                      value: {
                        ...en.value,
                        dev_type: val,
                      },
                    }))
                  }
                  style={{ marginTop: 4 }}
                >
                  {DEV_TYPES.map((opt) => (
                    <Select.Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            </div>

            {/* LN2 Message */}
            <div style={{ marginTop: 8 }}>
              <span
                style={{
                  ...sectionLabelStyle,
                  fontSize: 10,
                }}
              >
                Message
              </span>
              <NumatoFlatMessageFields
                msg={entry.value.msg}
                onChange={(updatedMsg) =>
                  updateR2M(idx, (en) => ({ ...en, value: { ...en.value, msg: updatedMsg } }))
                }
              />
            </div>
          </div>
        </div>
      ))}
      <Button type="dashed" onClick={addR2M} block>
        + Add Response to Message Mapping
      </Button>

      {/* ============================================= */}
      {/* MESSAGE TO COMMAND MAPPINGS */}
      <Divider orientation="left" plain>
        Message to Command Mappings ({cdo.message_to_cmd.length})
      </Divider>
      {cdo.message_to_cmd.map((entry, idx) => (
        <div key={`m2c_${idx}`} style={entryStyle}>
          <Button
            type="text"
            danger
            size="small"
            style={deleteButtonStyle}
            icon={<FontAwesomeIcon icon={faTrash} />}
            onClick={() => removeM2C(idx)}
          />
          <strong style={{ display: "block", marginBottom: 8 }}>Mapping #{idx + 1}</strong>

          {/* LN2 MESSAGE KEY */}
          <span style={sectionLabelStyle}>LN2 Message</span>
          <NumatoFlatMessageFields
            msg={entry.key}
            onChange={(updatedMsg) => updateM2C(idx, (e) => ({ ...e, key: updatedMsg }))}
          />

          {/* COMMAND VALUE */}
          <div style={{ marginTop: 12 }}>
            <span style={sectionLabelStyle}>Command</span>
            <div className="row">
              <div className="col-12 col-md-6 mt-2">
                <span className="generalStyles__inputLabel">Command Type</span>
                <Select
                  className="generalStyles__width100"
                  size="small"
                  value={entry.value.kind}
                  onChange={(val: string) =>
                    updateM2C(idx, (e) => ({
                      ...e,
                      value: buildDefaultCommand(val),
                    }))
                  }
                  style={{ marginTop: 4 }}
                >
                  {COMMAND_CATEGORIES.map((opt) => (
                    <Select.Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            </div>
            {renderCommandFields(entry.value, (updatedCmd) =>
              updateM2C(idx, (e) => ({
                ...e,
                value: updatedCmd,
              })),
            )}
          </div>
        </div>
      ))}
      <Button type="dashed" onClick={addM2C} block>
        + Add Message to Command Mapping
      </Button>
    </div>
  );
}
